import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/apiError";
import { ShieldCheck, Robot, Check, PencilSimple, Trash, SealCheck } from "@phosphor-icons/react";

const C = { teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", green: "#2CC5A8" };

export default function ReviewPanel({ deck, slideNum, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sr = (deck.review_state || {})[String(slideNum)];
  if (!sr) return null;
  const REVIEWABLE = ["assumed", "researched"];
  const pending = (sr.items || []).filter((i) => REVIEWABLE.includes(i.prov) && i.status === "pending");
  const item = pending[0];
  const attested = !!sr.attested_at;
  if (!item && attested) return null;

  const act = async (action, newText) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pitch/decks/${deck.deck_id}/review/${slideNum}/items/${item.id}`,
        { action, text: newText || "" });
      onUpdated(data);
      setEditing(false);
      setText("");
    } catch (err) {
      if (!err.isCreditShortage) toast.error(extractErrorMessage(err, "Could not update"));
    } finally {
      setBusy(false);
    }
  };

  const attest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pitch/decks/${deck.deck_id}/review/${slideNum}/attest`);
      onUpdated(data);
      toast.success(`Slide ${slideNum} attested.`);
    } catch (err) {
      if (!err.isCreditShortage) toast.error(extractErrorMessage(err, "Could not attest"));
    } finally {
      setBusy(false);
    }
  };

  if (!item) {
    // All assumed items resolved — attestation card.
    return (
      <div className="mb-5 rounded-lg border px-4 py-3.5 flex flex-wrap items-center gap-3"
        style={{ borderColor: "rgba(44,197,168,0.4)", backgroundColor: "rgba(44,197,168,0.07)" }}
        data-testid="attest-card">
        <SealCheck size={18} weight="fill" style={{ color: C.green }} className="shrink-0" />
        <div className="text-[13px] text-white/80 flex-1 min-w-[240px]">
          <span className="font-bold text-white">One last step for this slide:</span>{" "}
          confirm that everything on it is true and current to the best of your knowledge.
        </div>
        <button onClick={attest} disabled={busy} data-testid="attest-btn"
          className="px-4 py-2 rounded-md text-[12px] font-bold uppercase tracking-wider disabled:opacity-50"
          style={{ backgroundColor: C.green, color: "#00251f" }}>
          {busy ? "Saving…" : "I attest — true & current"}
        </button>
      </div>
    );
  }

  const total = (sr.items || []).filter((i) => REVIEWABLE.includes(i.prov)).length;
  const done = total - pending.length;
  const isResearched = item.prov === "researched";
  return (
    <div className="mb-5 rounded-lg border px-4 py-3.5"
      style={{ borderColor: "rgba(123,211,206,0.4)", backgroundColor: "rgba(10,139,133,0.08)" }}
      data-testid="review-panel">
      <div className="flex items-center gap-2 mb-2">
        <Robot size={16} weight="bold" style={{ color: C.tealSoft }} />
        <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: isResearched ? C.orange : C.tealSoft }}>
          {isResearched
            ? `David researched this — accept the source? · ${done}/${total} reviewed`
            : `David assumed this — your call · ${done}/${total} reviewed`}
        </span>
      </div>
      {editing ? (
        <div>
          <textarea autoFocus rows={2} value={text} onChange={(e) => setText(e.target.value)}
            data-testid="review-edit-input"
            className="w-full rounded-md px-3 py-2 text-sm text-white bg-white/[0.06] border border-white/10 focus:border-[#0A8B85] focus:outline-none" />
          <div className="mt-2 flex gap-2">
            <button onClick={() => act("edit", text)} disabled={busy || !text.trim()} data-testid="review-save-edit-btn"
              className="rounded-md px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-40"
              style={{ backgroundColor: C.teal, color: "#fff" }}>Save my wording</button>
            <button onClick={() => { setEditing(false); setText(""); }}
              className="rounded-md px-3 py-2 text-xs font-bold text-white/60 hover:text-white"
              style={{ border: "1px solid rgba(255,255,255,0.15)" }}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-[13px] text-white/85 leading-relaxed" data-testid="review-item-text">
            “{item.text}”
          </div>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <button onClick={() => act("confirm")} disabled={busy} data-testid="review-confirm-btn"
              className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              <Check size={13} weight="bold" /> That's right
            </button>
            <button onClick={() => { setEditing(true); setText(item.text); }} disabled={busy} data-testid="review-edit-btn"
              className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-bold text-white/70 hover:text-white disabled:opacity-50"
              style={{ border: "1px solid rgba(255,255,255,0.2)" }}>
              <PencilSimple size={13} weight="bold" /> Fix the wording
            </button>
            <button onClick={() => act("delete")} disabled={busy} data-testid="review-delete-btn"
              className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-bold text-white/50 hover:text-red-300 disabled:opacity-50"
              style={{ border: "1px solid rgba(255,255,255,0.12)" }}>
              <Trash size={13} weight="bold" /> Not true — remove
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function ReviewGateCard({ deck, onJump }) {
  const rs = deck.review_state || {};
  if (!Object.keys(rs).length) return null;
  let pending = 0;
  const unattested = [];
  Object.entries(rs).forEach(([num, s]) => {
    pending += (s.items || []).filter((i) => (i.prov === "assumed" || i.prov === "researched") && i.status === "pending").length;
    if (!s.attested_at) unattested.push(Number(num));
  });
  unattested.sort((a, b) => a - b);
  if (!pending && !unattested.length) {
    return (
      <div className="rounded-lg border px-4 py-3 mb-4 flex items-center gap-2.5"
        style={{ borderColor: "rgba(44,197,168,0.4)", backgroundColor: "rgba(44,197,168,0.07)" }}
        data-testid="review-gate-clear">
        <ShieldCheck size={17} weight="fill" style={{ color: C.green }} />
        <span className="text-[13px] text-white/80">
          <span className="font-bold text-white">Review complete.</span> Every assumed statement is confirmed and all slides are attested — export is unlocked.
        </span>
      </div>
    );
  }
  return (
    <div className="rounded-lg border px-4 py-3.5 mb-4"
      style={{ borderColor: "rgba(245,158,11,0.45)", backgroundColor: "rgba(245,158,11,0.08)" }}
      data-testid="review-gate-blocked">
      <div className="flex items-center gap-2 mb-1.5">
        <ShieldCheck size={17} weight="fill" style={{ color: C.orange }} />
        <span className="text-[13px] font-bold text-white">Review before export</span>
      </div>
      <div className="text-[12px] text-white/65 leading-relaxed">
        {pending > 0 && <>You still have <span className="font-bold text-white">{pending} assumed statement{pending > 1 ? "s" : ""}</span> to confirm. </>}
        {unattested.length > 0 && <>Slide{unattested.length > 1 ? "s" : ""} <span className="font-bold text-white">{unattested.join(", ")}</span> need{unattested.length > 1 ? "" : "s"} your attestation. </>}
        Funders get your words, not David's guesses — export unlocks when review is done.
      </div>
      {onJump && unattested.length > 0 && (
        <button onClick={() => onJump(unattested[0])} data-testid="review-gate-jump-btn"
          className="mt-2.5 rounded-md px-4 py-2 text-xs font-bold uppercase tracking-wider"
          style={{ backgroundColor: C.orange, color: "#000" }}>
          Take me there →
        </button>
      )}
    </div>
  );
}
